import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Package, RefreshCw, Plus, ExternalLink, Trash2, Search, Filter,
  CheckCircle, Truck, Inbox, Clock, MapPin, AlertCircle, Instagram, Facebook, Mail,
  X, ChevronDown, ChevronUp, Upload, Edit2
} from 'lucide-react';
import ImageLightbox from '../components/ImageLightbox';
import { toast } from 'sonner';
import { errMsg } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status visual config — matches the rest of the site's color language
const STATUS_META = {
  pre_transit:      { label: 'PRE-TRANSIT',     color: 'text-zinc-400',   bg: 'bg-zinc-500/15',   border: 'border-zinc-500/40', icon: Clock },
  in_transit:       { label: 'IN TRANSIT',      color: 'text-blue-400',   bg: 'bg-blue-500/15',   border: 'border-blue-500/40', icon: Truck },
  out_for_delivery: { label: 'OUT FOR DELIVERY',color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/40', icon: MapPin },
  delivered:        { label: 'DELIVERED',       color: 'text-tiffany-500',bg: 'bg-tiffany-500/15',border: 'border-tiffany-500/40', icon: Inbox },
  received:         { label: 'RECEIVED',        color: 'text-purple-400', bg: 'bg-purple-500/15', border: 'border-purple-500/40', icon: CheckCircle },
};
const SOURCE_LABELS = {
  trade: 'TRADE',
  submission: 'SELL-TO-US',
  restock: 'RESTOCK',
  supplies: 'SUPPLIES',
  other: 'OTHER',
};

export default function AdminInbound() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState({ packages: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [query, setQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showReceived, setShowReceived] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (sourceFilter) params.set('source_type', sourceFilter);
      if (showReceived) params.set('include_received', 'true');
      const r = await axios.get(`${API}/admin/inbound?${params.toString()}`, { headers: getAuthHeader() });
      setData(r.data || { packages: [], stats: null });
    } catch {
      toast.error('FAILED TO LOAD INBOUND');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, sourceFilter, showReceived]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/admin/inbound/refresh-all`, {}, { headers: getAuthHeader() });
      toast.success(`UPDATED ${r.data.updated} · ${r.data.newly_delivered} NEW DELIVERIES`);
      load();
    } catch {
      toast.error('REFRESH FAILED');
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.packages || [];
    return (data.packages || []).filter(p =>
      (p.tracking_number || '').toLowerCase().includes(q) ||
      (p.seller_name || '').toLowerCase().includes(q) ||
      (p.seller_instagram || '').toLowerCase().includes(q) ||
      (p.item_description || '').toLowerCase().includes(q)
    );
  }, [data.packages, query]);

  const stats = data.stats?.by_status || {};
  const totalActive = data.stats?.total_active || 0;
  const totalValue = data.stats?.total_value || 0;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="sticky top-0 z-10 bg-black border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-zinc-900"><ArrowLeft size={20} /></Link>
            <div>
              <h1 className="text-lg font-bold font-mono tracking-widest">INBOUND TRACKER</h1>
              <p className="text-xs text-zinc-500 font-mono">PACKAGES COMING TO YOU · {totalActive} ACTIVE · ${totalValue.toLocaleString()} VALUE</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={refreshAll} disabled={refreshing} className="p-2 hover:bg-zinc-900" title="Refresh all tracking" data-testid="refresh-all-inbound">
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-tiffany-500 text-black font-mono text-xs tracking-widest hover:bg-tiffany-400 flex items-center gap-1.5" data-testid="add-inbound-btn">
              <Plus size={14} /> ADD INBOUND
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Status pills */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(STATUS_META).filter(([k]) => k !== 'received').map(([k, m]) => {
            const count = stats[k] || 0;
            const Icon = m.icon;
            const active = statusFilter === k;
            return (
              <button
                key={k}
                onClick={() => setStatusFilter(active ? '' : k)}
                className={`border ${m.border} ${active ? m.bg : 'bg-black'} p-3 text-left hover:bg-zinc-950 transition-colors`}
                data-testid={`status-pill-${k}`}
              >
                <Icon className={`w-4 h-4 mb-1 ${m.color}`} />
                <p className={`font-mono text-xs font-bold ${m.color}`}>{count}</p>
                <p className="font-mono text-[10px] text-zinc-500 tracking-widest">{m.label}</p>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="SEARCH TRACKING · SELLER · ITEM"
              className="w-full bg-zinc-950 border border-zinc-800 pl-9 pr-3 py-2 font-mono text-xs text-white placeholder-zinc-600"
              data-testid="inbound-search"
            />
          </div>
          <div className="min-w-[180px]">
            <FilterSelect
              value={sourceFilter}
              onChange={setSourceFilter}
              options={[{ value: '', label: 'ALL SOURCES' }, ...Object.entries(SOURCE_LABELS).map(([k, v]) => ({ value: k, label: v }))]}
              testId="source-filter"
            />
          </div>
          <label className="flex items-center gap-2 font-mono text-xs text-zinc-400 cursor-pointer" data-testid="show-received-toggle">
            <input type="checkbox" checked={showReceived} onChange={(e) => setShowReceived(e.target.checked)} className="accent-tiffany-500" />
            SHOW RECEIVED
          </label>
        </div>

        {/* Card grid */}
        {loading ? (
          <p className="text-center py-12 font-mono text-zinc-500">LOADING_</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-zinc-900">
            <Package className="mx-auto mb-3 text-zinc-700" size={36} />
            <p className="font-mono text-zinc-500 tracking-widest text-sm">NO INBOUND PACKAGES</p>
            <button onClick={() => setShowAdd(true)} className="mt-4 font-mono text-xs text-tiffany-500 hover:underline">+ ADD FIRST PACKAGE</button>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(pkg => (
              <InboundCard key={pkg.id} pkg={pkg} onUpdate={load} />
            ))}
          </div>
        )}
      </div>

      {showAdd && <AddInboundModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

// =============================================================================
// Card (compact row · expandable details · edit)
// =============================================================================
function InboundCard({ pkg, onUpdate }) {
  const { getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const meta = STATUS_META[pkg.status] || STATUS_META.pre_transit;
  const StatusIcon = meta.icon;
  const photos = pkg.item_photos || [];
  const photo = photos[0];
  const photoUrl = photo
    ? (photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo)
    : null;

  const openLightbox = (e, idx) => {
    e.stopPropagation();
    setLightboxIdx(idx);
  };

  const markReceived = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Mark this package as RECEIVED?')) return;
    try {
      await axios.patch(`${API}/admin/inbound/${pkg.id}`, { received: true }, { headers: getAuthHeader() });
      toast.success('MARKED RECEIVED');
      onUpdate();
    } catch { toast.error('FAILED'); }
  };

  const refresh = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/admin/inbound/${pkg.id}/refresh`, {}, { headers: getAuthHeader() });
      toast.success('STATUS REFRESHED');
      onUpdate();
    } catch { toast.error('REFRESH FAILED'); }
  };

  const deletePkg = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this inbound record?')) return;
    try {
      await axios.delete(`${API}/admin/inbound/${pkg.id}`, { headers: getAuthHeader() });
      toast.success('DELETED');
      onUpdate();
    } catch { toast.error('DELETE FAILED'); }
  };

  const stop = (e) => e.stopPropagation();

  return (
    <>
      <div className={`border ${meta.border} bg-zinc-950 hover:bg-zinc-900/40 transition-colors`} data-testid={`inbound-card-${pkg.id}`}>
        {/* Compact row */}
        <div
          className="flex items-stretch cursor-pointer"
          onClick={() => setOpen(!open)}
          data-testid={`inbound-toggle-${pkg.id}`}
        >
          {/* Photo */}
          <div className="w-16 sm:w-20 h-16 sm:h-20 bg-black border-r border-zinc-800 flex items-center justify-center shrink-0 relative overflow-hidden">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={pkg.item_description || 'Inbound'}
                className="w-full h-full object-cover cursor-zoom-in"
                loading="lazy"
                onClick={(e) => openLightbox(e, 0)}
                data-testid={`row-photo-${pkg.id}`}
              />
            ) : (
              <Package className="text-zinc-700" size={22} />
            )}
            {photos.length > 1 && (
              <span className="absolute bottom-0 right-0 bg-black/80 text-zinc-300 font-mono text-[9px] px-1 pointer-events-none">+{photos.length - 1}</span>
            )}
          </div>

          {/* Middle */}
          <div className="flex-1 min-w-0 px-3 py-2 flex flex-col justify-center gap-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`px-1.5 py-0.5 ${meta.bg} ${meta.color} font-mono text-[9px] tracking-widest flex items-center gap-1 shrink-0`}>
                <StatusIcon size={9} />
                {meta.label}
              </span>
              <span className="px-1.5 py-0.5 bg-zinc-800 text-zinc-300 font-mono text-[9px] tracking-widest shrink-0">
                {SOURCE_LABELS[pkg.source_type] || pkg.source_type?.toUpperCase()}
              </span>
              {pkg.received && (
                <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-300 font-mono text-[9px] tracking-widest shrink-0">RECEIVED</span>
              )}
            </div>
            <p className="font-mono text-xs text-white truncate uppercase">
              {pkg.item_description || pkg.seller_name || pkg.tracking_number || '—'}
            </p>
            <p className="font-mono text-[10px] text-zinc-500 truncate">
              {pkg.seller_name ? `FROM ${pkg.seller_name.toUpperCase()} · ` : ''}{pkg.carrier} · {pkg.tracking_number}
            </p>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 px-3 shrink-0">
            {pkg.tracking_url && (
              <a
                href={pkg.tracking_url}
                target="_blank"
                rel="noreferrer"
                onClick={stop}
                className="hidden sm:flex items-center gap-1 border border-zinc-700 text-zinc-300 hover:border-teal-500 hover:text-teal-400 font-mono text-[10px] px-2 py-1 tracking-widest"
                data-testid={`track-url-${pkg.id}`}
                title="Open tracking page"
              >
                <ExternalLink size={10} /> TRACK
              </a>
            )}
            {typeof pkg.item_value === 'number' && (
              <div className="text-right">
                <p className="font-mono text-[9px] text-zinc-500 tracking-widest">VALUE</p>
                <p className="font-mono text-sm text-teal-400">${pkg.item_value.toFixed(0)}</p>
              </div>
            )}
            {open ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
          </div>
        </div>

        {/* Expanded */}
        {open && (
          <div className="border-t border-zinc-800 p-3 space-y-3 bg-black/40" onClick={stop}>
            {/* Seller contacts */}
            {(pkg.seller_instagram || pkg.seller_facebook || pkg.seller_email) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {pkg.seller_instagram && (
                  <a href={`https://instagram.com/${pkg.seller_instagram}`} target="_blank" rel="noreferrer" onClick={stop}
                    className="flex items-center gap-1 px-2 py-0.5 border border-pink-500/30 text-pink-400 hover:bg-pink-500/10 font-mono text-[10px]">
                    <Instagram size={10} />@{pkg.seller_instagram}
                  </a>
                )}
                {pkg.seller_facebook && (
                  <a href={`https://facebook.com/${pkg.seller_facebook}`} target="_blank" rel="noreferrer" onClick={stop}
                    className="flex items-center gap-1 px-2 py-0.5 border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 font-mono text-[10px]">
                    <Facebook size={10} />{pkg.seller_facebook}
                  </a>
                )}
                {pkg.seller_email && (
                  <a href={`mailto:${pkg.seller_email}`} onClick={stop}
                    className="flex items-center gap-1 px-2 py-0.5 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 font-mono text-[10px]">
                    <Mail size={10} />MAIL
                  </a>
                )}
              </div>
            )}

            {/* All photos */}
            {/* Photos — only show extras since first is already in the row thumb */}
            {photos.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {photos.slice(1).map((p, i) => (
                  <img
                    key={i}
                    src={p.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p}
                    alt=""
                    className="w-14 h-14 object-cover border border-zinc-800 cursor-zoom-in hover:opacity-80"
                    loading="lazy"
                    onClick={(e) => openLightbox(e, i + 1)}
                    data-testid={`expanded-photo-${pkg.id}-${i}`}
                  />
                ))}
              </div>
            )}

            {/* ETA */}
            {pkg.est_delivery_date && (
              <p className="font-mono text-[10px] text-zinc-400">ETA · {new Date(pkg.est_delivery_date).toLocaleDateString()}</p>
            )}

            {pkg.admin_notes && (
              <div className="bg-zinc-900 border-l-2 border-teal-500/50 p-2">
                <p className="font-mono text-[10px] text-zinc-500 mb-0.5">NOTE</p>
                <p className="font-mono text-[11px] text-zinc-300 italic break-words">{pkg.admin_notes}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-1.5">
              {pkg.tracking_url && (
                <a href={pkg.tracking_url} target="_blank" rel="noreferrer" onClick={stop}
                  className="sm:hidden flex items-center justify-center gap-1 border border-zinc-700 text-zinc-300 hover:border-teal-500 hover:text-teal-400 font-mono text-[10px] px-2 py-1 tracking-widest"
                >
                  <ExternalLink size={10} /> TRACK
                </a>
              )}
              <button onClick={refresh}
                className="flex items-center justify-center gap-1 border border-zinc-700 text-zinc-300 hover:border-blue-500 hover:text-blue-400 font-mono text-[10px] px-2 py-1 tracking-widest"
                data-testid={`refresh-${pkg.id}`}
              >
                <RefreshCw size={10} /> REFRESH
              </button>
              <button
                onClick={(e) => { stop(e); setShowEdit(true); }}
                className="flex items-center justify-center gap-1 border border-teal-500/40 text-teal-400 hover:bg-teal-500/10 font-mono text-[10px] px-2 py-1 tracking-widest"
                data-testid={`edit-${pkg.id}`}
              >
                <Edit2 size={10} /> EDIT
              </button>
              {pkg.status === 'delivered' && !pkg.received && (
                <button onClick={markReceived}
                  className="flex items-center justify-center gap-1 bg-purple-500/15 border border-purple-500/40 text-purple-300 hover:bg-purple-500/25 font-mono text-[10px] px-2 py-1 tracking-widest"
                  data-testid={`received-${pkg.id}`}
                >
                  <CheckCircle size={10} /> MARK RECEIVED
                </button>
              )}
              <button onClick={deletePkg}
                className="flex items-center justify-center gap-1 border border-red-500/20 text-red-400/60 hover:bg-red-500/10 hover:text-red-400 font-mono text-[10px] px-2 py-1 tracking-widest ml-auto"
                data-testid={`delete-${pkg.id}`}
              >
                <Trash2 size={10} /> DELETE
              </button>
            </div>
          </div>
        )}
      </div>
      {showEdit && (
        <EditInboundModal pkg={pkg} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); onUpdate(); }} />
      )}
      {lightboxIdx !== null && photos.length > 0 && (
        <ImageLightbox
          images={photos}
          initialIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  );
}

// =============================================================================
// Add modal
// =============================================================================
function AddInboundModal({ onClose, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState({
    tracking_number: '',
    carrier: 'USPS',
    source_type: 'trade',
    source_id: '',
    seller_name: '',
    seller_instagram: '',
    seller_facebook: '',
    seller_email: '',
    item_description: '',
    item_value: '',
    admin_notes: '',
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload/image`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setPhotos(p => [...p, r.data.url]);
      toast.success('PHOTO ADDED');
    } catch (err) {
      toast.error(errMsg(err, 'UPLOAD FAILED'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const save = async () => {
    if (!form.tracking_number.trim()) { toast.error('TRACKING # REQUIRED'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        item_value: form.item_value === '' ? null : parseFloat(form.item_value),
        item_photos: photos,
        source_id: form.source_id || null,
      };
      await axios.post(`${API}/admin/inbound`, payload, { headers: getAuthHeader() });
      toast.success('INBOUND ADDED');
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, 'CREATE FAILED'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-wider font-mono">ADD INBOUND PACKAGE</h2>
            <p className="text-xs text-zinc-500 font-mono">TRACK A PACKAGE COMING IN — TRADE, SELL-TO-US, RESTOCK</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="TRACKING #" value={form.tracking_number} onChange={(v) => set('tracking_number', v.toUpperCase())} testId="inb-tracking" required />
            <SelectField label="CARRIER" value={form.carrier} onChange={(v) => set('carrier', v)}
              options={[
                { value: 'USPS',  label: 'USPS' },
                { value: 'UPS',   label: 'UPS' },
                { value: 'FEDEX', label: 'FEDEX' },
                { value: 'DHL',   label: 'DHL' },
                { value: 'OTHER', label: 'OTHER' },
              ]}
              testId="inb-carrier"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <SelectField label="SOURCE" value={form.source_type} onChange={(v) => set('source_type', v)}
              options={[
                { value: 'trade',      label: 'TRADE' },
                { value: 'submission', label: 'SELL-TO-US' },
                { value: 'restock',    label: 'RESTOCK' },
                { value: 'supplies',   label: 'SUPPLIES' },
                { value: 'other',      label: 'OTHER' },
              ]}
              testId="inb-source"
            />
            <Field label="LINK ID (TRADE/SUB)" value={form.source_id} onChange={(v) => set('source_id', v)} placeholder="OPTIONAL" testId="inb-source-id" />
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <p className="font-mono text-[10px] text-teal-400 tracking-widest mb-3">SELLER INFO</p>
            <UserAutocomplete
              label="NAME"
              value={form.seller_name}
              onChange={(v) => set('seller_name', v.toUpperCase())}
              onPickUser={(u) => setForm(f => ({
                ...f,
                seller_name: (u.full_name || u.email || '').toUpperCase(),
                seller_email: u.email || f.seller_email,
              }))}
              testId="inb-seller"
            />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="EMAIL" value={form.seller_email} onChange={(v) => set('seller_email', v)} testId="inb-seller-email" placeholder="EMAIL@EXAMPLE.COM" />
              <Field label="INSTAGRAM" value={form.seller_instagram} onChange={(v) => set('seller_instagram', v)} placeholder="@HANDLE" testId="inb-seller-ig" />
              <Field label="FACEBOOK" value={form.seller_facebook} onChange={(v) => set('seller_facebook', v)} placeholder="PROFILE" testId="inb-seller-fb" />
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <p className="font-mono text-[10px] text-teal-400 tracking-widest mb-3">ITEM</p>
            <Field label="DESCRIPTION" value={form.item_description} onChange={(v) => set('item_description', v.toUpperCase())} testId="inb-item-desc" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="VALUE ($)" value={form.item_value} onChange={(v) => set('item_value', v)} type="number" placeholder="0.00" testId="inb-value" />
            </div>

            <div className="mt-4">
              <label className="block text-xs text-zinc-500 mb-1 font-mono">PHOTOS · {photos.length}</label>
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-zinc-700">
                      <img src={p.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setPhotos(ph => ph.filter((_, idx) => idx !== i))}
                        className="absolute top-1 right-1 p-0.5 bg-black/70 rounded-full hover:bg-red-500"
                        data-testid={`remove-photo-${i}`}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="block border border-dashed border-zinc-700 hover:border-teal-500 rounded-lg p-3 text-center cursor-pointer transition-colors">
                <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" disabled={uploading} data-testid="inb-photo-upload" />
                {uploading ? (
                  <span className="text-sm text-teal-400 font-mono flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></span>
                    UPLOADING...
                  </span>
                ) : (
                  <span className="text-sm text-zinc-500 font-mono flex items-center justify-center gap-2">
                    <Upload size={14} /> ADD PHOTO
                  </span>
                )}
              </label>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <Field label="ADMIN NOTES" value={form.admin_notes} onChange={(v) => set('admin_notes', v)} multiline testId="inb-notes" placeholder="INTERNAL NOTES" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-zinc-900 border-t border-zinc-800 p-4 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-700 rounded-lg text-zinc-300 font-mono text-xs tracking-widest hover:bg-zinc-800">CANCEL</button>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-black font-mono text-xs font-bold tracking-widest rounded-lg flex items-center gap-2"
            data-testid="save-inbound-btn"
          >
            {saving && <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
            {saving ? 'SAVING...' : 'SAVE INBOUND'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', required, multiline, testId }) {
  const cls = "w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono uppercase text-sm";
  return (
    <div>
      <label className="block text-xs text-zinc-500 mb-1 font-mono">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {multiline ? (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={2} className={`${cls} resize-none`} data-testid={testId} />
      ) : (
        <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls} data-testid={testId} />
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const display = options.find(o => o.value === value)?.label;
  return (
    <div className="relative">
      <label className="block text-xs text-zinc-500 mb-1 font-mono">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-left font-mono uppercase flex items-center justify-between focus:border-teal-500 focus:outline-none text-white"
        data-testid={testId}
      >
        <span>{display || ''}</span>
        <ChevronDown size={16} className={`text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 w-full bg-zinc-900 border border-zinc-700 rounded-lg max-h-56 overflow-y-auto shadow-xl mt-1">
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`w-full px-3 py-2 text-left font-mono uppercase text-sm border-b border-zinc-800 last:border-0 ${
                  value === opt.value ? 'bg-teal-500/20 text-teal-400' : 'text-white hover:bg-zinc-800'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Compact filter dropdown (no label) used in the toolbar.
function FilterSelect({ value, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const display = options.find(o => o.value === value)?.label || options[0]?.label;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-left font-mono uppercase flex items-center justify-between focus:border-teal-500 focus:outline-none text-white min-w-[160px]"
        data-testid={testId}
      >
        <span>{display}</span>
        <ChevronDown size={14} className={`text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 w-full bg-zinc-900 border border-zinc-700 rounded-lg max-h-56 overflow-y-auto shadow-xl mt-1">
            {options.map((opt) => (
              <button
                key={opt.value || 'all'}
                type="button"
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`w-full px-3 py-2 text-left font-mono uppercase text-xs border-b border-zinc-800 last:border-0 ${
                  value === opt.value ? 'bg-teal-500/20 text-teal-400' : 'text-white hover:bg-zinc-800'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Edit modal — reuses Field/SelectField; mirrors the Add modal layout
// =============================================================================
function EditInboundModal({ pkg, onClose, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState({
    tracking_number: pkg.tracking_number || '',
    carrier: pkg.carrier || 'USPS',
    source_type: pkg.source_type || 'trade',
    source_id: pkg.source_id || '',
    seller_name: pkg.seller_name || '',
    seller_instagram: pkg.seller_instagram || '',
    seller_facebook: pkg.seller_facebook || '',
    seller_email: pkg.seller_email || '',
    item_description: pkg.item_description || '',
    item_value: pkg.item_value ?? '',
    admin_notes: pkg.admin_notes || '',
    received: !!pkg.received,
  });
  const [photos, setPhotos] = useState(pkg.item_photos || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload/image`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setPhotos(p => [...p, r.data.url]);
      toast.success('PHOTO ADDED');
    } catch (err) {
      toast.error(errMsg(err, 'UPLOAD FAILED'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const save = async () => {
    if (!form.tracking_number.trim()) { toast.error('TRACKING # REQUIRED'); return; }
    setSaving(true);
    try {
      const payload = {
        tracking_number: form.tracking_number,
        carrier: form.carrier,
        source_type: form.source_type,
        source_id: form.source_id || null,
        seller_name: form.seller_name,
        seller_instagram: form.seller_instagram,
        seller_facebook: form.seller_facebook,
        seller_email: form.seller_email,
        item_description: form.item_description,
        item_value: form.item_value === '' ? null : parseFloat(form.item_value),
        item_photos: photos,
        admin_notes: form.admin_notes,
        received: form.received,
      };
      await axios.patch(`${API}/admin/inbound/${pkg.id}`, payload, { headers: getAuthHeader() });
      toast.success('INBOUND UPDATED');
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, 'UPDATE FAILED'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-bold tracking-wider font-mono">EDIT INBOUND</h2>
            <p className="text-xs text-zinc-500 font-mono truncate">{pkg.tracking_number}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="TRACKING #" value={form.tracking_number} onChange={(v) => set('tracking_number', v.toUpperCase())} testId="edit-tracking" required />
            <SelectField label="CARRIER" value={form.carrier} onChange={(v) => set('carrier', v)}
              options={[
                { value: 'USPS', label: 'USPS' }, { value: 'UPS', label: 'UPS' },
                { value: 'FEDEX', label: 'FEDEX' }, { value: 'DHL', label: 'DHL' }, { value: 'OTHER', label: 'OTHER' },
              ]} testId="edit-carrier" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <SelectField label="SOURCE" value={form.source_type} onChange={(v) => set('source_type', v)}
              options={[
                { value: 'trade', label: 'TRADE' }, { value: 'submission', label: 'SELL-TO-US' },
                { value: 'restock', label: 'RESTOCK' }, { value: 'supplies', label: 'SUPPLIES' }, { value: 'other', label: 'OTHER' },
              ]} testId="edit-source" />
            <Field label="LINK ID (TRADE/SUB)" value={form.source_id} onChange={(v) => set('source_id', v)} placeholder="OPTIONAL" testId="edit-source-id" />
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <p className="font-mono text-[10px] text-teal-400 tracking-widest mb-3">SELLER INFO</p>
            <UserAutocomplete
              label="NAME"
              value={form.seller_name}
              onChange={(v) => set('seller_name', v.toUpperCase())}
              onPickUser={(u) => setForm(f => ({
                ...f,
                seller_name: (u.full_name || u.email || '').toUpperCase(),
                seller_email: u.email || f.seller_email,
              }))}
              testId="edit-seller"
            />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="EMAIL" value={form.seller_email} onChange={(v) => set('seller_email', v)} testId="edit-seller-email" placeholder="EMAIL@EXAMPLE.COM" />
              <Field label="INSTAGRAM" value={form.seller_instagram} onChange={(v) => set('seller_instagram', v)} placeholder="@HANDLE" testId="edit-seller-ig" />
              <Field label="FACEBOOK" value={form.seller_facebook} onChange={(v) => set('seller_facebook', v)} placeholder="PROFILE" testId="edit-seller-fb" />
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <p className="font-mono text-[10px] text-teal-400 tracking-widest mb-3">ITEM</p>
            <Field label="DESCRIPTION" value={form.item_description} onChange={(v) => set('item_description', v.toUpperCase())} testId="edit-item-desc" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="VALUE ($)" value={form.item_value} onChange={(v) => set('item_value', v)} type="number" placeholder="0.00" testId="edit-value" />
            </div>

            <div className="mt-4">
              <label className="block text-xs text-zinc-500 mb-1 font-mono">PHOTOS · {photos.length}</label>
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-zinc-700">
                      <img src={p.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setPhotos(ph => ph.filter((_, idx) => idx !== i))}
                        className="absolute top-1 right-1 p-0.5 bg-black/70 rounded-full hover:bg-red-500"
                        data-testid={`edit-remove-photo-${i}`}><X size={10} /></button>
                    </div>
                  ))}
                </div>
              )}
              <label className="block border border-dashed border-zinc-700 hover:border-teal-500 rounded-lg p-3 text-center cursor-pointer transition-colors">
                <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" disabled={uploading} data-testid="edit-photo-upload" />
                {uploading ? (
                  <span className="text-sm text-teal-400 font-mono flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></span>
                    UPLOADING...
                  </span>
                ) : (
                  <span className="text-sm text-zinc-500 font-mono flex items-center justify-center gap-2"><Upload size={14} /> ADD PHOTO</span>
                )}
              </label>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <Field label="ADMIN NOTES" value={form.admin_notes} onChange={(v) => set('admin_notes', v)} multiline testId="edit-notes" placeholder="INTERNAL NOTES" />
            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input type="checkbox" checked={form.received} onChange={(e) => set('received', e.target.checked)} className="accent-teal-500" data-testid="edit-received-toggle" />
              <span className="font-mono text-xs text-zinc-300">MARK AS RECEIVED</span>
            </label>
          </div>
        </div>

        <div className="sticky bottom-0 bg-zinc-900 border-t border-zinc-800 p-4 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-700 rounded-lg text-zinc-300 font-mono text-xs tracking-widest hover:bg-zinc-800">CANCEL</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-black font-mono text-xs font-bold tracking-widest rounded-lg flex items-center gap-2"
            data-testid="save-edit-inbound-btn">
            {saving && <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
            {saving ? 'SAVING...' : 'SAVE CHANGES'}
          </button>
        </div>
      </div>
    </div>
  );
}



// =============================================================================
// User autocomplete — search existing users by name/email; admin can also
// type in a free-form name (gets passed straight through to seller_name).
// =============================================================================
function UserAutocomplete({ label, value, onChange, onPickUser, testId }) {
  const { getAuthHeader } = useAuth();
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);

  // Debounced search whenever the input text changes
  useEffect(() => {
    const q = (value || '').trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/admin/users/search?q=${encodeURIComponent(q)}&limit=8`, { headers: getAuthHeader() });
        if (!cancelled) setResults(r.data?.users || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="relative">
      <label className="block text-xs text-zinc-500 mb-1 font-mono">{label}</label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="SEARCH USERS OR TYPE A NEW NAME"
        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono uppercase text-sm"
        data-testid={testId}
        autoComplete="off"
      />
      {open && (value || '').trim().length >= 2 && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-h-64 overflow-y-auto">
            {searching && (
              <div className="px-3 py-2 font-mono text-[11px] text-zinc-500">SEARCHING…</div>
            )}
            {!searching && results.length === 0 && (
              <div className="px-3 py-2 font-mono text-[11px] text-zinc-500">NO MATCHES — WILL BE SAVED AS A NEW NAME</div>
            )}
            {results.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => { onPickUser(u); setOpen(false); setResults([]); }}
                className="w-full px-3 py-2 text-left hover:bg-zinc-800 border-b border-zinc-800 last:border-0"
                data-testid={`pick-user-${u.id}`}
              >
                <p className="font-mono text-xs text-white uppercase">{u.full_name || '—'}</p>
                <p className="font-mono text-[10px] text-zinc-500 truncate">{u.email}{u.is_verified ? ' · VERIFIED' : ''}</p>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
